/**
 * Connectors — manage reverse-tunnel agents (admins + "allow create connections").
 *
 * A connector is the inverse of an SSH bastion: instead of Ninocat dialing OUT
 * to reach a private MySQL, you download a small agent, run it on the LAN, and
 * it dials OUT to Ninocat and stays connected. Connections then route through
 * it (Connection editor → "Connect via"). NAT-friendly: no inbound ports.
 *
 * The pairing token is shown once at create/rotate time, but is stored
 * vault-encrypted server-side, so the personalised agent stays downloadable at
 * any time (the ⬇ on each row). Access is by GRANT (like forms/imports): you see
 * your own connectors + ones granted to you; admins see all. The owner or an admin
 * shares a connector with specific users (Share), and only they may rotate/revoke/
 * rename/delete it.
 */
import { createSignal, createResource, onCleanup, For, Show } from 'solid-js';
import { currentUser } from '../lib/auth-store.js';
import Modal from './Modal.jsx';
import { toast } from '../lib/toast.js';
import { copyText } from '../lib/export.js';
import {
  listConnectors, createConnector, renameConnector, rotateConnectorToken,
  revokeConnector, unrevokeConnector, deleteConnector, connectorDownloadUrl,
  getConnectorGrants, setConnectorGrants, listGrantableUsers,
} from '../lib/connectors.js';

export default function Connectors(props) {
  const [list, { refetch }] = createResource(listConnectors);
  const [busyId, setBusyId] = createSignal(0);
  const [renaming, setRenaming] = createSignal(null); // { id, name }
  // The one-time token reveal after create/rotate: { id, name, token, rotated }.
  const [paired, setPaired] = createSignal(null);
  const [downloadFor, setDownloadFor] = createSignal(null); // connector for the per-row download modal
  const [sharing, setSharing] = createSignal(null); // { connector, users, granted:Set, loading }
  const [newName, setNewName] = createSignal('');

  // Share access — owner/admin picks which users may use the connector.
  const openShare = async (c) => {
    setSharing({ connector: c, users: [], granted: new Set(), loading: true });
    try {
      const [users, granted] = await Promise.all([listGrantableUsers(), getConnectorGrants(c.id)]);
      setSharing({ connector: c, users, granted: new Set(granted), loading: false });
    } catch (err) { toast.error('Couldn’t load access: ' + err.message); setSharing(null); }
  };
  const toggleGrant = (uid) => {
    const s = sharing();
    const g = new Set(s.granted);
    g.has(uid) ? g.delete(uid) : g.add(uid);
    setSharing({ ...s, granted: g });
  };
  const saveGrants = async () => {
    const s = sharing();
    try {
      await setConnectorGrants(s.connector.id, [...s.granted]);
      toast.success('Access updated.');
      setSharing(null);
    } catch (err) { toast.error('Save failed: ' + err.message); }
  };

  // Poll so online/offline + last-seen stay fresh while the panel is open.
  const poll = setInterval(refetch, 10000);
  onCleanup(() => clearInterval(poll));

  const create = async () => {
    const name = newName().trim();
    if (!name) { toast.error('Name required.'); return; }
    try {
      const r = await createConnector(name);
      setNewName('');
      setPaired({ id: r.connector.id, name, token: r.token, rotated: false });
      refetch();
    } catch (err) { toast.error('Create failed: ' + err.message); }
  };

  const rotate = async (c) => {
    if (!confirm(`Rotate the token for "${c.name}"? The current agent is disconnected and must be re-downloaded or re-paired.`)) return;
    setBusyId(c.id);
    try {
      const r = await rotateConnectorToken(c.id);
      setPaired({ id: c.id, name: c.name, token: r.token, rotated: true });
      refetch();
    } catch (err) { toast.error('Rotate failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const toggleRevoke = async (c) => {
    setBusyId(c.id);
    try {
      if (c.revoked) { await unrevokeConnector(c.id); toast.success(`"${c.name}" re-enabled.`); }
      else { await revokeConnector(c.id); toast.success(`"${c.name}" revoked — its agent is disconnected.`); }
      refetch();
    } catch (err) { toast.error('Failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete connector "${c.name}"? This fails if any connection still uses it.`)) return;
    setBusyId(c.id);
    try { await deleteConnector(c.id); toast.success('Connector deleted.'); refetch(); }
    catch (err) { toast.error('Delete failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const saveRename = async () => {
    const r = renaming();
    if (!r || !r.name.trim()) return;
    try { await renameConnector(r.id, r.name.trim()); setRenaming(null); refetch(); }
    catch (err) { toast.error('Rename failed: ' + err.message); }
  };

  const lastSeen = (c) => {
    if (c.status?.online) return 'now';
    if (!c.lastSeenAt) return 'never';
    const secs = Math.floor(Date.now() / 1000) - c.lastSeenAt;
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };

  return (
    <Modal open={true} onClose={props.onClose} title="Connectors" wide>
      <p class="muted" style="font-size:12px;margin:0 0 12px;">
        Reach a MySQL server on a private LAN (behind NAT) without inbound ports.
        Create a connector, download its agent, run it on the LAN — it dials out to
        Ninocat. Then point a connection at it via <em>Connect via</em>.
      </p>

      <div style="display:flex;gap:8px;justify-content:flex-end;margin:0 0 10px;">
        <input class="input" placeholder="New connector name (e.g. office-lan)" style="max-width:260px;"
               value={newName()} onInput={(e) => setNewName(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        <button class="btn" onClick={create}>+ Create</button>
      </div>

      <Show when={!list.loading} fallback={<div class="muted">Loading…</div>}>
        <Show when={(list() || []).length === 0}>
          <div class="muted" style="font-size:12px;">No connectors yet — create one to tunnel a connection through a downloaded agent.</div>
        </Show>
        <table class="settings-backups mono" style="width:100%;">
          <thead><tr>
            <th>Name</th><th>Status</th><th>Last seen</th><th>Agent</th><th>Owner</th><th></th>
          </tr></thead>
          <tbody>
            <For each={list() || []}>
              {(c) => (
                <tr>
                  <td>{c.name}</td>
                  <td>
                    {/* .conn-status is the shared 9px status DOT (Connections page);
                        the label sits beside it and never wraps. */}
                    <Show when={c.revoked} fallback={
                      <span class="connector-state">
                        <span class={`conn-status ${c.status?.online ? 'online' : 'offline'}`} />
                        {c.status?.online ? 'online' : 'offline'}
                      </span>
                    }>
                      <span class="connector-state muted">
                        <span class="conn-status unknown" />
                        revoked
                      </span>
                    </Show>
                  </td>
                  <td class="muted">{lastSeen(c)}</td>
                  <td class="muted">{c.status?.agentVersion || '—'}</td>
                  <td class="muted">{c.ownerUserId === currentUser()?.id ? 'you' : (c.ownerName || '—')}</td>
                  <td style="text-align:right;white-space:nowrap;">
                    {/* Owner/admin only — the agent embeds the pairing token (server-enforced). */}
                    <Show when={c.canManage}>
                      <button class="btn btn-sm" onClick={() => setDownloadFor(c)} title="Download the paired agent (any platform)">⬇ Agent</button>
                      <button class="btn btn-sm btn-ghost" onClick={() => openShare(c)} title="Choose which users may use this connector">Share</button>
                      <button class="btn btn-sm btn-ghost" disabled={busyId() === c.id} onClick={() => rotate(c)} title="Issue a new token (disconnects the current agent)">Rotate</button>
                      <button class="btn btn-sm btn-ghost" onClick={() => setRenaming({ id: c.id, name: c.name })}>Rename</button>
                      <button class="btn btn-sm btn-ghost" disabled={busyId() === c.id} onClick={() => toggleRevoke(c)}>
                        {c.revoked ? 'Unrevoke' : 'Revoke'}
                      </button>
                      <button class="btn btn-sm btn-danger" disabled={busyId() === c.id} onClick={() => remove(c)}>Delete</button>
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>

      {/* One-time token reveal + download — after create or rotate. */}
      <Show when={paired()}>
        <Modal open={true} onClose={() => setPaired(null)} title={paired().rotated ? 'New token issued' : 'Connector created'}>
          <p class="muted" style="font-size:12px;margin:0 0 10px;">
            Download the agent for <strong>{paired().name}</strong> and run it on the LAN machine that can reach your MySQL.
            On first run it <strong>installs itself as a background service</strong> (Windows: a UAC admin prompt; Linux: <code>sudo</code>),
            so it auto-starts at boot and reconnects on its own. The token is <strong>baked into the download</strong> —
            shown only once, so copy it if you'll pair manually.
          </p>
          <div class="settings-row"><label>Token</label>
            <input class="input mono" readonly value={paired().token} style="font-size:11.5px;"
                   onFocus={(e) => e.target.select()} /></div>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px;flex-wrap:wrap;align-items:center;">
            <button class="btn btn-ghost btn-sm" onClick={async () => { try { await copyText(paired().token); toast.success('Token copied.'); } catch (e) { toast.error('Copy failed: ' + e.message); } }}>Copy token</button>
            {/* download attribute is required or @solidjs/router swallows the click (PROJECT_NOTES §5) */}
            <a class="btn btn-sm" download href={connectorDownloadUrl(paired().id, 'windows', 'amd64')}>⬇ Windows 64-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(paired().id, 'windows', '386')}>Windows 32-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(paired().id, 'linux', 'amd64')}>Linux 64-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(paired().id, 'linux', 'arm64')}>Linux ARM64</a>
          </div>
          <p class="muted" style="font-size:11px;margin:12px 0 0;">
            ⚠ The download contains the token — treat it like a secret. You can re-download the agent any time from the ⬇ Agent button on its row.
            To stop &amp; remove the service later, run the agent with <code>--uninstall</code>, or <code>--foreground</code> to run it without installing.
          </p>
        </Modal>
      </Show>

      {/* Rename */}
      <Show when={renaming()}>
        <Modal open={true} onClose={() => setRenaming(null)} title="Rename connector">
          <div class="settings-row"><label>Name</label>
            <input class="input" value={renaming().name} autofocus
                   onInput={(e) => setRenaming({ ...renaming(), name: e.target.value })}
                   onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }} /></div>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setRenaming(null)}>Cancel</button>
            <button class="btn" onClick={saveRename}>Save</button>
          </div>
        </Modal>
      </Show>

      {/* Re-download the agent any time — the token is vault-stored, not transient. */}
      <Show when={downloadFor()}>
        <Modal open={true} onClose={() => setDownloadFor(null)} title={`Download agent — ${downloadFor().name}`}>
          <p class="muted" style="font-size:12px;margin:0 0 10px;">
            Run it on the LAN machine that can reach your MySQL. On first run it installs itself as a
            background service (Windows: a UAC prompt; Linux: <code>sudo</code>) and reconnects automatically.
            The pairing token is baked in — treat the file like a secret.
          </p>
          <div style="display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;align-items:center;">
            <a class="btn btn-sm" download href={connectorDownloadUrl(downloadFor().id, 'windows', 'amd64')}>⬇ Windows 64-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(downloadFor().id, 'windows', '386')}>Windows 32-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(downloadFor().id, 'linux', 'amd64')}>Linux 64-bit</a>
            <a class="btn btn-sm btn-ghost" download href={connectorDownloadUrl(downloadFor().id, 'linux', 'arm64')}>Linux ARM64</a>
          </div>
        </Modal>
      </Show>

      {/* Share access — grant this connector to specific users (owner or admin). */}
      <Show when={sharing()}>
        <Modal open={true} onClose={() => setSharing(null)} title={`Share access — ${sharing().connector.name}`}>
          <p class="muted" style="font-size:12px;margin:0 0 10px;">
            Pick which users may use this connector — attach it to their connections and download its agent.
            You always keep access to your own; only you and admins can manage it.
          </p>
          <Show when={!sharing().loading} fallback={<div class="muted">Loading…</div>}>
            <Show when={sharing().users.length > 0}
                  fallback={<div class="muted" style="font-size:12px;">No other connection-creating users to grant to.</div>}>
              <div class="grant-objects" style="max-height:320px;overflow:auto;">
                <For each={sharing().users}>
                  {(usr) => (
                    <label class="checkbox-label">
                      <input type="checkbox" checked={sharing().granted.has(usr.id)} onChange={() => toggleGrant(usr.id)} />
                      {usr.username}
                    </label>
                  )}
                </For>
              </div>
            </Show>
          </Show>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setSharing(null)}>Cancel</button>
            <button class="btn" onClick={saveGrants} disabled={sharing().loading}>Save</button>
          </div>
        </Modal>
      </Show>
    </Modal>
  );
}
